import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Compte a rebours anniversaire: jours restants";
const DESCRIPTION =
  "Affichez le temps exact avant votre prochain anniversaire en jours, heures, minutes et secondes. Ideal pour planifier invitations, budget et organisation de votre evenement.";
const KEYWORDS =
  "compte a rebours anniversaire, jours avant anniversaire, prochain anniversaire, calculatrice anniversaire, jours restants";
const OG_URL = "https://do-calculate.com/calculator/fr/birthday/";

// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice de Compte à Rebours d'Anniversaire",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Calculatrice d'Anniversaire",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Comment savoir combien de temps avant mon anniversaire?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Entrez votre date de naissance dans la calculatrice et vous obtiendrez immédiatement le compte à rebours précis de votre prochain anniversaire en mois, jours, heures, minutes et secondes.",
        },
      },
      {
        "@type": "Question",
        name: "Le compte à rebours est-il mis à jour automatiquement?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Oui, le compte à rebours se met à jour automatiquement chaque seconde pour vous afficher un temps précis jusqu'à votre anniversaire.",
        },
      },
    ],
  },
];

const GREGORIAN_MONTHS = [
  "Janvier / Janvier", "Février / Février", "Mars / Mars", "Avril / Avril",
  "Mai / Mai", "Juin / Juin", "Juillet / Juillet", "Août / Août",
  "Septembre / Septembre", "Octobre / Octobre", "Novembre / Novembre", "Décembre / Décembre"
];

const DAYS_OF_WEEK = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

function BirthdayCalculator() {
  const [birthDateInput, setBirthDateInput] = useState({
    day: "1",
    month: "1",
    year: "1990"
  });

  const [countdown, setCountdown] = useState(null);
  const [error, setError] = useState(null);
  const [isToday, setIsToday] = useState(false);

  const calculateCountdown = () => {
    const selectedDate = new Date(
      parseInt(birthDateInput.year),
      parseInt(birthDateInput.month) - 1,
      parseInt(birthDateInput.day)
    );

    if (isNaN(selectedDate.getTime())) {
      setError("Veuillez entrer une date d'anniversaire valide");
      setCountdown(null);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setError("La date d'anniversaire ne peut pas être dans le futur");
      setCountdown(null);
      return;
    }

    let nextBirthday = new Date(today.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    if (nextBirthday <= today) {
      nextBirthday = new Date(today.getFullYear() + 1, selectedDate.getMonth(), selectedDate.getDate());
    }

    const timeRemaining = nextBirthday - new Date();
    const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeRemaining / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((timeRemaining / (1000 * 60)) % 60);
    const seconds = Math.floor((timeRemaining / 1000) % 60);

    const isTodayCheckDate = new Date();
    isTodayCheckDate.setHours(0, 0, 0, 0);
    const isActuallyToday = isTodayCheckDate.getTime() === selectedDate.getTime();

    setCountdown({
      days,
      hours,
      minutes,
      seconds,
      nextBirthday: nextBirthday.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
      birthDateFormatted: selectedDate.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
    });
    setError(null);
    setIsToday(isActuallyToday);
  };

  useEffect(() => {
    if (!countdown) return;

    const timer = setInterval(() => {
      calculateCountdown();
    }, 1000);

    return () => clearInterval(timer);
  }, [birthDateInput]);

  return (
    <div style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center p-3">
            <h1 style={{ fontSize: "20px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light p-3">
            <p>
              Entrez votre date de naissance pour afficher le compte a rebours exact jusqu'a votre prochain anniversaire. Le resultat se met a jour en continu pour vous donner un suivi precis en temps reel.
            </p>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle p-3 bg-light rounded">
              <div className="p-2 mb-3 bg-success text-white">
                <strong>Entrez votre date de naissance</strong>
              </div>

              <div className="mb-3">
                <label><strong>Jour:</strong></label>
                <select
                  className="form-control"
                  value={birthDateInput.day}
                  onChange={(e) => setBirthDateInput({ ...birthDateInput, day: e.target.value })}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label><strong>Mois:</strong></label>
                <select
                  className="form-control"
                  value={birthDateInput.month}
                  onChange={(e) => setBirthDateInput({ ...birthDateInput, month: e.target.value })}
                >
                  {GREGORIAN_MONTHS.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label><strong>Année:</strong></label>
                <select
                  className="form-control"
                  value={birthDateInput.year}
                  onChange={(e) => setBirthDateInput({ ...birthDateInput, year: e.target.value })}
                >
                  {Array.from({ length: 150 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div className="text-center mb-4">
                <button
                  className="btn btn-success btn-lg px-5"
                  onClick={calculateCountdown}
                >
                  Démarrer le Compte à Rebours
                </button>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}

              {isToday && (
                <div className="alert alert-info">
                  Joyeux anniversaire! 🎉
                </div>
              )}

              {countdown && (
                <div className="alert alert-success mt-4">
                  <h3>Compte à Rebours jusqu'à votre Anniversaire:</h3>
                  <div className="countdown-display" style={{ fontSize: "48px", fontWeight: "bold", color: "#28a745", textAlign: "center", margin: "20px 0" }}>
                    {countdown.days}j {countdown.hours}h {countdown.minutes}m {countdown.seconds}s
                  </div>
                  <div className="mt-3">
                    <p><strong>Détails:</strong></p>
                    <ul>
                      <li>Date de naissance: {countdown.birthDateFormatted}</li>
                      <li>Prochain anniversaire: {countdown.nextBirthday}</li>
                      <li>Jours restants: {countdown.days}</li>
                      <li>Heures restantes: {countdown.hours}</li>
                      <li>Minutes restantes: {countdown.minutes}</li>
                      <li>Secondes restantes: {countdown.seconds}</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
          <br />
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
          <RelatedLinks />
          <br />
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Comment fonctionne le compte a rebours anniversaire</h3></div>
              <div className="info-section-body">
                <p>L'outil calcule automatiquement le temps qui vous separe de votre prochain anniversaire a partir de votre date de naissance. Il affiche un compteur detaille en jours, heures, minutes et secondes, utile pour anticiper vos preparatifs.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Pourquoi calculer les jours restants avant son anniversaire</h3></div>
              <div className="info-section-body">
                <p>Connaitre les jours restants permet d'organiser un budget, reserver un lieu, envoyer des invitations ou programmer un rappel. C'est aussi pratique pour les parents qui planifient un anniversaire d'enfant plusieurs semaines a l'avance.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Idées pour fêter son anniversaire</div>
              <div className="info-section-body">
                <ul>
                  <li><strong>Réunion en famille :</strong> repas maison ou restaurant avec les proches</li>
                  <li><strong>Soirée entre amis :</strong> bar, bowling, escape game, pique-nique</li>
                  <li><strong>Week-end de voyage :</strong> mini-escapade pour marquer le coup</li>
                  <li><strong>Célébration virtuelle :</strong> appel vidéo groupé pour les proches éloignés</li>
                  <li><strong>Anniversaire d'enfant :</strong> thème personnalisé, animateur, ateliers créatifs, parc d'attractions</li>
                  <li><strong>Journée bien-être :</strong> spa, massages, activité détente pour se faire plaisir</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Planifier son anniversaire à l'avance</div>
              <div className="info-section-body">
                <ul>
                  <li><strong>1 mois avant :</strong> décider du type de fête, établir la liste des invités, choisir le lieu et le budget</li>
                  <li><strong>2 semaines avant :</strong> envoyer les invitations, réserver le restaurant ou la salle, commander le gâteau</li>
                  <li><strong>1 semaine avant :</strong> confirmer les réservations, acheter les décorations, préparer la playlist</li>
                  <li><strong>La veille :</strong> préparer les aliments, installer les décorations, préparer l'appareil photo</li>
                  <li><strong>Le jour J :</strong> derniers ajustements, accueillir les invités, capturer les souvenirs</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Les anniversaires dans le monde</div>
              <div className="info-section-body">
                <ul>
                  <li><strong>Mexique :</strong> la piñata remplie de bonbons que les enfants cassent les yeux bandés</li>
                  <li><strong>Brésil :</strong> on pince l'oreille de l'anniversaire autant de fois que son âge</li>
                  <li><strong>Chine :</strong> les nouilles longues symbolisent la longévité — on ne les coupe pas</li>
                  <li><strong>Japon :</strong> seuls les âges 3, 5 et 7 ans sont célébrés de façon traditionnelle (Shichi-Go-San)</li>
                  <li><strong>Pays-Bas :</strong> la chaise du fêté est décorée de fleurs et de ballons</li>
                  <li><strong>Russie :</strong> on offre une tarte aux initiales plutôt qu'un gâteau avec des bougies</li>
                  <li><strong>Canada :</strong> on frotte du beurre sur le nez de l'anniversaire pour porter chance</li>
                </ul>
                <p><strong>Traditions universelles :</strong> souffler les bougies, chanter « Joyeux anniversaire », offrir des cadeaux, prendre des photos.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Conseils pour un anniversaire mémorable</div>
              <div className="info-section-body">
                <ul>
                  <li>Soyez authentique — choisissez ce qui vous ressemble vraiment</li>
                  <li>Anticipez le décompte pour savourer l'attente</li>
                  <li>Documentez les moments importants (photos, courte vidéo)</li>
                  <li>Partagez la joie avec vos proches, même à distance</li>
                  <li>Profitez-en pour faire le point et vous fixer de nouveaux objectifs</li>
                </ul>
                <p><strong>L'essentiel :</strong> l'anniversaire parfait, c'est celui qui vous rend heureux — qu'il soit intime ou festif.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Questions fréquentes</div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "16px" }}><strong>Comment savoir combien de jours il reste avant mon anniversaire ?</strong></h3>
                <p>Entrez simplement votre date de naissance dans la calculatrice. Le compte à rebours se met à jour automatiquement à chaque visite.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Puis-je suivre les anniversaires d'autres personnes ?</strong></h3>
                <p>Oui, entrez n'importe quelle date de naissance pour voir le compte à rebours jusqu'au prochain anniversaire de cette personne.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Combien de temps à l'avance faut-il commencer à planifier ?</strong></h3>
                <p>Pour un anniversaire intime, 2 à 3 semaines suffisent. Pour une grande fête avec réservation de lieu ou beaucoup d'invités, prévoyez 4 à 6 semaines minimum.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Que faire si mon anniversaire tombe un 29 février ?</strong></h3>
                <p>Les personnes nées le 29 février (les « bissextiles ») fêtent généralement leur anniversaire le 28 février ou le 1er mars les années non bissextiles. La calculatrice gère correctement ce cas particulier.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BirthdayCalculator;
